<script setup lang="ts">
import Editor from "primevue/editor";
import { watchEffect } from "vue";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = string;
interface PropsType extends BaseProps<ModelType> {
    formId?: string;
    placeholder?: string;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
});

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

watchEffect(() => {
    if (model.value === "" || model.value === null) {
        model.value = undefined;
    }
});
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <div class="flex flex-row items-start rns-input-static">
                <div class="px-1 grow" v-html="model" />
                <Buttons.BtnUpdate v-if="state.updatable" class="mr-2 self-center" @click="actions.onUpdate" />
                <Buttons.BtnRemove v-if="state.removable" class="mr-2 self-center" @click="actions.onDelete" />
            </div>
        </template>
        <template v-else>
            <Editor
                v-model="model"
                :disabled="props.disabled"
                editorStyle="height: 96px"
                fluid
                :form="props.formId"
                :inputId="state.id"
                :invalid="state.hasError"
                :placeholder="props.placeholder ?? `Masukkan ${props.label}`"
                :required="props.required"
            >
                <template #toolbar>
                    <div class="flex flex-row justify-center gap-5">
                        <button v-tooltip.bottom="'Tautan'" class="ql-link"></button>
                        <button v-tooltip.bottom="'Sisipkan Gambar'" class="ql-image"></button>
                        <button v-tooltip.bottom="'Hapus Format'" class="ql-clean"></button>
                        <Buttons.BtnReset v-if="state.clearable && state.hasValue" @click="actions.onReset" />
                    </div>
                </template>
            </Editor>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-input-static {
    @apply text-gray-700! bg-transparent! shadow-none! px-1! pt-2.25! min-h-21;
    @apply border rounded-none border-gray-400;
}
</style>
